import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { AlphaBadge, BuildStamp, PlayForFun } from '../components/AlphaNotice.js';
import { Link, useSearchParams } from 'react-router-dom';
import {
  MAX_NAME_LENGTH,
  normalizeRoomCode,
  profileHex,
  profileInitials,
  type BaccaratSideBetId,
  type BaccaratSpot,
  type CalloutMode,
  type CasinoLeaderboardEntry,
  type CasinoTheme,
  type ChipSetId,
  type DeckId,
  type FeltId,
  type GameType,
  type PartyPlan,
  type HoldemActionType,
  type CasinoMemberView,
  type JoinResult,
  type PrivateHand,
  type RoomState,
  type CrapsBetId,
  type CrapsPressMode,
  type RouletteBetId,
  type RouletteBetNum,
   type SideBetId,
   type StatsWindow,
 } from '@tv-casino/shared';
import { BaccaratBet } from '../components/BaccaratBet.js';
import { FeedbackButton } from '../components/FeedbackButton.js';
import { YourCasinos } from '../components/YourCasinos.js';
import { CardRow } from '../components/CardView.js';
import { CasinoLobby } from '../components/CasinoLobby.js';
import { casinoLobbyModel, type LobbyTab } from '@tv-casino/viewmodels/components/casinoLobbyModel.js';
import { floorMarketModel } from '@tv-casino/viewmodels/components/floorMarketModel.js';
import { CasinoOwnerPanel } from '../components/CasinoOwnerPanel.js';
import { casinoOwnerPanelModel } from '@tv-casino/viewmodels/components/casinoOwnerPanelModel.js';
import { ChipBet } from '../components/ChipBet.js';
import { GamePollCard } from '../components/GamePollCard.js';
import { PartyStrip } from '../components/PartyStrip.js';
import { FloatAction, useOffscreenWatch } from '../components/FloatAction.js';
import { HoldemActions } from '../components/HoldemActions.js';
import {
  HostPanel,
  type BaccaratRulesPatch,
  type CrapsRulesPatch,
  type KenoRulesPatch,
  type RouletteRulesPatch,
  type RulesPatch,
  type ThreecardRulesPatch,
} from '../components/HostPanel.js';
import { CrapsBet } from '../components/CrapsBet.js';
import { DeviceAudio } from '../components/DeviceAudio.js';
import { CrapsDice } from '../components/CrapsDice.js';
import { CrapsTable } from '../components/CrapsTable.js';
import { useCrapsRoomReading } from '@tv-casino/viewmodels/components/crapsThrow.js';
import { KenoBet } from '../components/KenoBet.js';
import { KenoBoard, useKenoReveal } from '../components/KenoBoard.js';
import { RouletteBet } from '../components/RouletteBet.js';
import { RouletteWheel } from '../components/RouletteWheel.js';
import { SideBetPicker } from '../components/SideBetPicker.js';
import { ThreeCardBet } from '../components/ThreeCardBet.js';
import {
  fetchBalanceTimeline,
  fetchLeaderboard,
  fetchMemberView,
  fetchMyHistory,
  fetchMyLedger,
  lookupCasino,
  setCasinoTheme,
  type CasinoLookup,
} from '../lib/casinoApi.js';
import {
  useCrapsCues,
  useRouletteCues,
  useTableAmbience,
  useTableCues,
} from '../lib/tableSound.js';
import { useDealerCallouts } from '../lib/useDealerCallouts.js';
import { useGameSplash } from '@tv-casino/viewmodels/lib/useGameSplash.js';
import { GameSplash } from '../components/GameSplash.js';
import { PHONE_SPLASH_MS, gameSplashVM } from '@tv-casino/viewmodels/components/gameSplashModel.js';
import { readCalloutMode, setSoundScope, writeCalloutMode } from '../lib/sound.js';
import { themeStyle } from '../lib/themeStyle.js';
import { SeatHuesContext, seatHues } from '../lib/playerColors.js';
import { ProfileBadge } from '../components/ProfileBadge.js';
import { joinProfileModel } from '@tv-casino/viewmodels/routes/joinProfileModel.js';
import { getSocket, request, RequestError } from '../lib/socket.js';
import { memberIdentity, playerIdentity, statsWindowPref, type StoredMembership } from '../lib/storage.js';
import { useOwnsCasino } from '../lib/useOwnsCasino.js';
import { memberMarketModel } from '@tv-casino/viewmodels/components/memberMarketModel.js';
import { parseStatsWindow } from '@tv-casino/viewmodels/components/windowPickerModel.js';
import { playModel, playAnimFocusKey, type PlayView } from '@tv-casino/viewmodels/routes/playModel.js';
import styles from './Play.module.css';
import type {
  BalanceSeries,
  LedgerTapeResponse,
  RoundHistoryResponse,
} from '@tv-casino/shared';

type PlayAction =
  | 'player_hit'
  | 'player_stand'
  | 'player_double'
  | 'player_split'
  | 'player_surrender';

/**
 * How long a tab may sit in the background before its socket is written off
 * as dead on wake. One server heartbeat is 10s (SOCKET_PING_INTERVAL_MS), so
 * a nap that long has already missed one.
 */
const STALE_SOCKET_MS = 10_000;

/** Mirrors the server's clock so the phone shows the same countdown. */
function Countdown({ endsAt, label }: { endsAt: number; label: string }) {
  const [left, setLeft] = useState(() => Math.max(0, endsAt - Date.now()));
  useEffect(() => {
    setLeft(Math.max(0, endsAt - Date.now()));
    const timer = setInterval(() => setLeft(Math.max(0, endsAt - Date.now())), 250);
    return () => clearInterval(timer);
  }, [endsAt]);
  return (
    <p className={left <= 5_000 ? styles.countdownUrgent : styles.countdown}>
      {label} · {Math.ceil(left / 1000)}s
    </p>
  );
}

export function Play() {
  // This page is a player's seat, not the felt: its mute and its dealer level
  // live under the device's own storage, never the TV's. First statement, so
  // every audio control below reads the right seat's switch from the off.
  setSoundScope('device');
  const [params] = useSearchParams();
  const saved = useRef(playerIdentity.get()).current;
  const urlRoom = normalizeRoomCode(params.get('room') ?? '');
  const urlTable = normalizeRoomCode(params.get('table') ?? '');
  /** Set by a casino or table QR: the join rides with membership. */
  const urlCasino = (params.get('casino') ?? '').trim().toUpperCase() || null;

  const [code, setCode] = useState(() =>
    urlTable || urlRoom || (urlCasino ? '' : normalizeRoomCode(saved.code ?? '')),
  );
  const [name, setName] = useState(saved.name ?? '');
  const [seat, setSeat] = useState<JoinResult | null>(null);
  const [state, setState] = useState<RoomState | null>(null);
  const [hand, setHand] = useState<PrivateHand | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alreadySeated, setAlreadySeated] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(getSocket().connected);
  /** The seat the controls point at when the table is not waiting on a specific one. */
  const [focusSeat, setFocusSeat] = useState<number | null>(null);
  /** Side-bet stakes staged locally; they ride along with the next player_bet. */
  const [sideBets, setSideBets] = useState<Partial<Record<SideBetId, number>>>({});
  /** The baccarat spot this seat is backing, staged until the chips go down. */
  const [spot, setSpot] = useState<BaccaratSpot | null>(null);
  const [pairBets, setPairBets] = useState<Partial<Record<BaccaratSideBetId | 'pair_plus', number>>>({});
  /**
   * The server does not publish who has already answered the insurance offer,
   * so remember it per seat until the window closes.
   */
  const [answeredInsurance, setAnsweredInsurance] = useState<number[]>([]);
  /**
   * Which of the phone's three screens is showing: this seat's controls, the
   * large-text view of the whole table, or the casino lobby. One piece of
   * state, so the screens can never be half-open on top of each other.
   */
  const [view, setView] = useState<PlayView>('controls');
  /**
   * This phone's own dealer level, next to its volume: whatever the host has
   * the TV set to, this seat hears the amount of dealer it asked for.
   */
  const [calloutMode, setCalloutMode] = useState<CalloutMode>(() => readCalloutMode());
  const changeCalloutMode = useCallback((mode: CalloutMode) => {
    writeCalloutMode(mode);
    setCalloutMode(mode);
  }, []);
  /** The roller stage — wheel or dice — so a spin/roll can pull it into view. */
  const stageRef = useRef<HTMLElement>(null);
  /** Last animation key we already focused for; ignores room_state echoes. */
  const animFocusSeen = useRef<string | null>(null);
  /** True until the stage is on screen and we have scrolled it into view. */
  const animFocusPending = useRef(false);
  /** False once the player leaves or is evicted, so we stop reclaiming a seat. */
  const wantsSeat = useRef(true);
  /** Mirrors busy for callbacks that must not stack a second join. */
  const inflight = useRef(false);
  /** Bounded backoff for automatic rejoins after a lock or a dropped radio. */
  const rejoin = useRef<{ attempts: number; timer: ReturnType<typeof setTimeout> | undefined }>({
    attempts: 0,
    timer: undefined,
  });
  /** From the QR, or learned from the join ack when the code was typed. */
  const [casinoCode, setCasinoCode] = useState<string | null>(urlCasino);
  /** Signed-in owner: Deal and settings show on this table, not only on /casino. */
  const { owns: isOwner, playerName: hostName, session } = useOwnsCasino(casinoCode);
  /**
   * This phone's membership in the casino: the id the server knows it by and
   * the token that proves it. The server issues both when the phone first
   * sits down, so until then there is nothing to read a card with — the
   * lobby shows the newcomer state instead.
   */
  const [membership, setMembership] = useState<StoredMembership | null>(() =>
    urlCasino ? memberIdentity.get(urlCasino) : null,
  );
  const [casino, setCasino] = useState<CasinoLookup | null>(null);
  const [me, setMe] = useState<CasinoMemberView | null>(null);
  /** The lobby's two screens: the table picker, or the night's numbers. */
  const [lobbyTab, setLobbyTab] = useState<LobbyTab>('tables');
  /**
   * The window every market card reads — this member's desk, the floor, the
   * TV board. Saved so a phone that cut the floor to 7 days finds the TV on
   * the same page.
   */
  const [floorWindow, setFloorWindow] = useState<StatsWindow>(() =>
    parseStatsWindow(statsWindowPref.get()),
  );
  /** Personal balance line + both audit tapes for the lobby desk. */
  const [series, setSeries] = useState<BalanceSeries[]>([]);
  const [myRounds, setMyRounds] = useState<RoundHistoryResponse | null>(null);
  const [myLedger, setMyLedger] = useState<LedgerTapeResponse | null>(null);
  /** The public board behind the floor: who else is up, who is down. */
  const [board, setBoard] = useState<CasinoLeaderboardEntry[] | null>(null);
  const [tradeLimit, setTradeLimit] = useState(20);
  const [ledgerLimit, setLedgerLimit] = useState(40);
  const [tradesBusy, setTradesBusy] = useState(false);
  const [ledgerBusy, setLedgerBusy] = useState(false);

  const join = useCallback(
    async (joinCode: string, joinName: string, playerId?: string, reclaim = false) => {
      setBusy(true);
      inflight.current = true;
      setError(null);
      try {
        // Under a casino the phone presents its membership: chips live on the
        // member row and follow this player across tables and sessions. The
        // id alone is not a credential — the token that came with it is.
        // With no casino link, the last venue's membership rides along.
        const venueGuess = casinoCode ?? memberIdentity.lastVenue();
        const held = casinoCode
          ? membership
          : venueGuess
            ? memberIdentity.get(venueGuess)
            : null;
        const res = await request<JoinResult>((cb) =>
          getSocket().emit(
            'player_join',
            {
              code: joinCode,
              name: joinName,
              playerId,
              reclaim,
              casinoCode: venueGuess ?? undefined,
              memberId: held?.id,
              memberToken: held?.token ?? undefined,
            },
            cb,
          ),
        );
        rejoin.current.attempts = 0;
        // The server names the membership and hands over its token the once;
        // from here on this phone proves itself with what it just stored.
        // A table code typed without the casino still seats a member: the ack
        // names the venue, and the phone keeps it from here on.
        const venue = res.casinoCode ?? casinoCode;
        if (venue && res.memberId) {
          // A rejoin that kept its seat gets no fresh token: reuse the one
          // stored under the venue the ack names, which is not always the
          // venue this phone guessed (a bare TV's table, after a refresh).
          const stored = venue === venueGuess ? held : memberIdentity.get(venue);
          const kept = stored?.id === res.memberId ? stored.token : null;
          const opened = { id: res.memberId, token: res.memberToken ?? kept ?? null };
          memberIdentity.set(venue, opened, { remember: !res.adhoc });
          if (venue !== casinoCode) setCasinoCode(venue);
          // Keep the same object when nothing changed: `join` depends on
          // membership, and a fresh identity every ack re-ran the socket
          // effect, which rejoined, which acked again — a busy flicker loop.
          setMembership((prev) =>
            prev && prev.id === opened.id && prev.token === opened.token ? prev : opened,
          );
        }
        playerIdentity.set(res.playerId, res.name, res.code);
        wantsSeat.current = true;
        setAlreadySeated(false);
        setSeat(res);
        setNotice(null);
        return true;
      } catch (e) {
        if (e instanceof RequestError && e.code === 'already_seated') {
          setAlreadySeated(true);
        }
        // A lost session (cleared storage, new device) can still take its old
        // seat back — but only once the player confirms the takeover.
        if (e instanceof RequestError && e.code === 'name_taken_offline') {
          setError(e.message);
          if (window.confirm(`Reclaim ${joinName}'s seat at the table?`)) {
            return join(joinCode, joinName, playerId, true);
          }
          return false;
        }
        setError(e instanceof Error ? e.message : 'Could not join.');
        // An identity rejoin keeps trying: a locked phone or a flaky network
        // should not cost the seat. Definitive answers stop the crawl.
        // A membership this phone cannot prove is dropped rather than retried:
        // the next join opens a fresh one instead of hammering a refusal.
        const refusedVenue = casinoCode ?? memberIdentity.lastVenue();
        if (e instanceof RequestError && e.code === 'member_forbidden' && refusedVenue) {
          memberIdentity.clear(refusedVenue);
          setMembership(null);
        }
        const definitive =
          e instanceof RequestError &&
          [
            'room_not_found',
            'room_full',
            'name_taken',
            'member_forbidden',
            'already_seated',
          ].includes(e.code);
        if (playerId && !definitive && wantsSeat.current && rejoin.current.attempts < 5) {
          const attempt = ++rejoin.current.attempts;
          clearTimeout(rejoin.current.timer);
          rejoin.current.timer = setTimeout(
            () => wantsSeat.current && void join(joinCode, joinName, playerId, reclaim),
            Math.min(2 ** attempt * 1000, 10_000),
          );
        }
        return false;
      } finally {
        inflight.current = false;
        setBusy(false);
      }
    },
    [casinoCode, membership],
  );

  /**
   * The host plays under the name on their account, so the field fills
   * itself once the session resolves. The server insists on it either way —
   * this only saves the owner from typing a name that would be overridden.
   */
  useEffect(() => {
    if (hostName) setName(hostName);
  }, [hostName]);

  /**
   * A signed-in guest starts from the name their account plays under, but it
   * stays theirs to change: only the owner's name is reserved. A name this
   * phone already remembers is left alone.
   */
  useEffect(() => {
    if (!session || hostName) return;
    setName((prev) => (prev.trim() ? prev : session.playerName || session.name));
  }, [session, hostName]);

  /** The casino lineup. Each table row carries its own room code. */
  const refreshCasino = useCallback(async () => {
    if (!casinoCode) return;
    try {
      const found = await lookupCasino(casinoCode);
      setCasino(found);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No casino with that code.');
    }
  }, [casinoCode]);

  /**
   * The owner dresses the venue: the patch rides the owner-only REST route,
   * then the lineup re-read brings this phone its own new theme — the roots
   * read `casino.theme`, so every screen under them repaints at once. The TV
   * and the other phones follow on the `casino_state` push.
   */
  const changeTheme = async (patch: Partial<CasinoTheme>) => {
    if (!casinoCode) return;
    setBusy(true);
    try {
      // A bare TV's venue has no owner: its table host dresses it over the
      // socket, and the server checks they still hold the table.
      if (casino?.adhoc) {
        await request<CasinoTheme>((cb) => getSocket().emit('host_set_theme', patch, cb));
      } else {
        await setCasinoTheme(casinoCode, patch);
      }
      await refreshCasino();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the theme.');
    } finally {
      setBusy(false);
    }
  };
  const changeFelt = (felt: FeltId) => void changeTheme({ felt });
  const changeChips = (chips: ChipSetId) => void changeTheme({ chips });
  const changeDeck = (deck: DeckId) => void changeTheme({ deck });

  /** This phone's own chips and counters — the lobby's balance/stat card. */
  const refreshMe = useCallback(async () => {
    if (!casinoCode || !membership) return;
    try {
      setMe(await fetchMemberView(casinoCode, membership.id, membership.token));
    } catch {
      // The card falls back to the newcomer state; the table is unaffected.
    }
  }, [casinoCode, membership]);

  /**
   * The floor's public inputs — the leaderboard and every member's balance
   * line — are room knowledge, readable without a membership, so a newcomer
   * sees the room too. Failures leave the last board standing.
   */
  const refreshFloor = useCallback(async () => {
    if (!casinoCode) return;
    try {
      const [rows, timeline] = await Promise.all([
        fetchLeaderboard(casinoCode, floorWindow),
        fetchBalanceTimeline(casinoCode, floorWindow),
      ]);
      if (Array.isArray(rows)) setBoard(rows);
      setSeries(Array.isArray(timeline.series) ? timeline.series : []);
    } catch {
      // Keep the last board; the floor section stays quiet rather than erroring.
    }
  }, [casinoCode, floorWindow]);

  /** Cut every market card to a new window; the floor refetches off the state. */
  const changeFloorWindow = (window: StatsWindow) => {
    setFloorWindow(window);
    statsWindowPref.set(window);
  };

  /**
   * Quote desk inputs kept behind this phone's credential: the settled-round
   * blotter and the raw ledger. Failures leave the last good read.
   */
  const refreshMarket = useCallback(async () => {
    if (!casinoCode || !membership) return;
    try {
      const [hist, tape] = await Promise.all([
        fetchMyHistory(casinoCode, membership.id, membership.token, tradeLimit),
        fetchMyLedger(casinoCode, membership.id, membership.token, ledgerLimit),
      ]);
      if (hist) setMyRounds(hist);
      if (tape) setMyLedger(tape);
    } catch {
      // Keep the last desk; the lobby still has the balance card fallback.
    } finally {
      setTradesBusy(false);
      setLedgerBusy(false);
    }
  }, [casinoCode, membership, tradeLimit, ledgerLimit]);

  useEffect(() => {
    void refreshCasino();
  }, [refreshCasino]);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  useEffect(() => {
    void refreshMarket();
  }, [refreshMarket]);

  useEffect(() => {
    void refreshFloor();
  }, [refreshFloor]);

  /**
   * Bind the owner's socket to the table they picked so room_state follows the
   * selection. Host controls themselves wait until they sit (My controls) —
   * Deal on the lineup would start a round before anyone joined. Skip once
   * they sit: player_join already binds, and a second owner_watch would
   * overwrite the player role.
   */
  useEffect(() => {
    if (!casinoCode || !isOwner || seat || code.length !== 4) return;
    void request((cb) => getSocket().emit('owner_watch', { casinoCode, table: code }, cb)).catch(
      () => undefined,
    );
  }, [casinoCode, isOwner, seat, code]);

  /** Crawl back into the stored seat, if the table still has it. */
  const rejoinFromIdentity = useCallback(() => {
    if (!wantsSeat.current || inflight.current) return;
    const stored = playerIdentity.get();
    if (!stored.playerId || !stored.name || !stored.code) return;
    // A QR for a different room wins over whatever this phone sat at last.
    if (casinoCode && (!urlTable || urlTable !== stored.code)) return;
    if (!casinoCode && urlRoom && urlRoom !== stored.code) return;
    void join(stored.code, stored.name, stored.playerId);
  }, [join, casinoCode, urlRoom, urlTable]);
  /** Latest rejoin for the socket listeners, so they bind once per mount. */
  const rejoinRef = useRef(rejoinFromIdentity);
  useEffect(() => {
    rejoinRef.current = rejoinFromIdentity;
  }, [rejoinFromIdentity]);

  useEffect(() => {
    const rejoinFromIdentity = () => rejoinRef.current();
    /**
     * A locked phone kills its socket without a goodbye; on return, force the
     * reconnect instead of waiting out socket.io's own backoff schedule.
     *
     * `connected` cannot be trusted to say so. iOS freezes a backgrounded tab
     * outright: the connection dies, but the heartbeat that would notice is
     * suspended along with every other timer, so the flag still reads true on
     * wake. A bet emitted then goes into a dead pipe and its ack never lands —
     * the phone sits on "The table did not answer" for eight seconds and the
     * wager is gone. Any nap long enough to have missed a heartbeat is treated
     * as a death: throw the stale transport away and open a fresh one.
     */
    let hiddenSince: number | null = null;
    const nudge = () => {
      const socket = getSocket();
      const napped = hiddenSince !== null && Date.now() - hiddenSince >= STALE_SOCKET_MS;
      hiddenSince = null;
      if (!socket.connected) {
        socket.connect();
        return;
      }
      if (!napped) {
        rejoinFromIdentity();
        return;
      }
      // Whatever is queued against the dead transport must not survive: it
      // would flush on the new socket id, before the seat is reclaimed.
      socket.sendBuffer.length = 0;
      socket.disconnect();
      socket.connect(); // 'connect' takes the seat back via playerId.
    };
    const socket = getSocket();
    const onState = (next: RoomState) => setState(next);
    const onHand = (h: PrivateHand | null) => setHand(h);
    const onClosed = ({ reason }: { reason: string }) => {
      setSeat(null);
      setState(null);
      setHand(null);
      setNotice(reason);
      playerIdentity.clearSeat();
      wantsSeat.current = false; // Do not silently crawl back into a closed room.
    };
    const onConnect = () => {
      setOnline(true);
      // A refresh or a dropped signal reclaims the same seat via playerId.
      rejoinFromIdentity();
    };
    const onDisconnect = () => setOnline(false);
    const onVisible = () => {
      if (document.visibilityState === 'visible') nudge();
      else hiddenSince = Date.now();
    };

    socket.on('room_state', onState);
    socket.on('private_hand', onHand);
    socket.on('room_closed', onClosed);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', nudge);
    window.addEventListener('online', nudge);
    if (socket.connected) onConnect();

    return () => {
      socket.off('room_state', onState);
      socket.off('private_hand', onHand);
      socket.off('room_closed', onClosed);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', nudge);
      window.removeEventListener('online', nudge);
      clearTimeout(rejoin.current.timer);
    };
  }, []);

  useEffect(() => {
    if (state?.phase !== 'insurance') setAnsweredInsurance([]);
  }, [state?.phase]);

  /**
   * Settle writes the ledger and the counters, so the card is stale exactly
   * once per round — refresh it there and nowhere else.
   */
  useEffect(() => {
    if (state?.phase !== 'payout') return;
    void refreshMe();
    void refreshMarket();
    void refreshFloor();
    void refreshCasino();
  }, [state?.phase, state?.round, refreshMe, refreshMarket, refreshFloor, refreshCasino]);

  useEffect(() => {
    if (state?.phase === 'betting') return;
    // A new round should not inherit the last one's staged side bets.
    setSideBets({});
    setPairBets({});
  }, [state?.phase]);

  /**
   * When the wheel starts or the dice go out / tumble, pull the roller into
   * view. The felt is tall enough that a player mid-bet is usually scrolled
   * past the stage; a spin they never see is a wasted animation. Switching
   * back to My controls first so Casino / The table cannot hide it.
   *
   * One layout effect owns the whole beat: an effect/layout split would set
   * the pending flag after layout had already run, and a controls-tab spin
   * would never scroll.
   */
  // The table reads the roll only once the dice have landed: while they are in
  // the air the model sees the world as it stood before the throw. The focus
  // key reads it too, so a roll that ends the hand still scrolls like any other.
  const readState = useCrapsRoomReading(state);
  // The phone runs the same soundtrack the TV does, at whatever level this
  // device chose — silent until its own audio control is unmuted. The dealer
  // reads the same broadcasts and makes the same calls, but how loud — and
  // how chatty — that voice is stays this device's choice: the host's TV
  // switches stop at the TV.
  useCrapsCues(readState);
  useRouletteCues(state);
  // A pressed craps win stacks chips back on the number; the click waits for
  // the dice like the flash does. Every other game reads straight through.
  useTableCues(readState);
  useTableAmbience(state);
  useDealerCallouts(state, { volume: 'device', mode: calloutMode });
  const animFocusKey = playAnimFocusKey(readState);
  useLayoutEffect(() => {
    if (!animFocusKey) return;

    if (animFocusKey !== animFocusSeen.current) {
      animFocusSeen.current = animFocusKey;
      animFocusPending.current = true;
    }
    if (!animFocusPending.current) return;

    if (view !== 'controls') {
      setView('controls');
      return;
    }

    const el = stageRef.current;
    if (!el) return;
    animFocusPending.current = false;
    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof el.focus === 'function') el.focus({ preventScroll: true });
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    }
  }, [animFocusKey, view]);

  // Every enable/disable, banner and label below is decided by the model.
  const vm = useMemo(
    () =>
      playModel(readState, hand, seat?.playerId ?? null, {
        joinSeats: seat?.seats,
        focusSeat,
        answeredInsurance,
        notice,
        online,
        isOwner,
        view,
        hasCasino: casinoCode !== null,
      }),
    [readState, hand, seat, focusSeat, answeredInsurance, notice, online, isOwner, view, casinoCode],
  );
  /** Seat colours with profile picks resolved, for every felt that draws chips. */
  const hues = useMemo(() => seatHues(state), [state]);
  // The table changing game swaps this phone's whole control strip. The card
  // covers the swap so nothing is tapped blind, says what is being dealt now,
  // and goes on a tap for a player who already knows.
  const { cue: splash, dismiss: dismissSplash } = useGameSplash(
    state?.gameType ?? null,
    PHONE_SPLASH_MS,
  );
  const joinProfile = useMemo(() => joinProfileModel(session, casino?.name ?? null), [session, casino]);
  /** The server's word on this seat: a signed-in account sat down here. */
  const seatHasProfile = Boolean(
    seat && state?.players.some((p) => p.playerId === seat.playerId && p.hasProfile),
  );
  // The turn row sits at the bottom of a long scroll, under the rail and the
  // side bets; while the clock is on this seat, the turn floats when it is
  // away. A split pushes it further down still — two hands of cards above it —
  // so the float has to carry Hit as well, or the second hand looks finished.
  const standWatch = useOffscreenWatch<HTMLButtonElement>(vm.isMyTurn && vm.canStand);
  // The phone lights the keno draw on the same deadline the TV does.
  const kenoReveal = useKenoReveal(
    vm.kenoView?.boardDraw ?? null,
    vm.kenoView?.settled ?? false,
    vm.deadline?.endsAt ?? null,
  );

  /** The lobby card reads from the same pure-model discipline as the table. */
  const lobbyVm = useMemo(
    () => casinoLobbyModel(casino, me, { selectedTableCode: code, alreadySeated, tab: lobbyTab }),
    [casino, me, code, alreadySeated, lobbyTab],
  );
  /** The owner's theme pickers, off the lookup the lobby already holds. */
  const ownerPanelVm = useMemo(() => casinoOwnerPanelModel(casino), [casino]);
  /** A bare TV's table: a hidden venue with no owner, run by the table host. */
  const adhocVenue = seat?.adhoc === true || casino?.adhoc === true;
  /** Who holds the theme pickers: the owner, or at a bare TV the table host. */
  const dressesVenue = isOwner || (adhocVenue && vm.amHost);
  /** This phone's own line off the public timeline, for its desk sparkline. */
  const mySeries = useMemo(
    () => series.find((s) => s.memberId === (membership?.id ?? '')) ?? null,
    [series, membership],
  );
  const marketVm = useMemo(
    () =>
      memberMarketModel({
        me,
        series: mySeries,
        rounds: myRounds?.entries ?? [],
        roundsHasMore: myRounds?.hasMore ?? false,
        ledger: myLedger?.entries ?? [],
        ledgerHasMore: myLedger?.hasMore ?? false,
      }),
    [me, mySeries, myRounds, myLedger],
  );
  /** Everyone else's public desk: the floor under this member's own. */
  const floorVm = useMemo(
    () => floorMarketModel(board, series, membership?.id ?? null),
    [board, series, membership],
  );

  const loadMoreTrades = () => {
    setTradesBusy(true);
    setTradeLimit((n) => Math.min(n + 20, 200));
  };
  const loadMoreLedger = () => {
    setLedgerBusy(true);
    setLedgerLimit((n) => Math.min(n + 40, 200));
  };

  const stagedSideBets = Object.values(sideBets).reduce((sum, n) => sum + (n ?? 0), 0);
  const stagedPairBets = Object.values(pairBets).reduce((sum, n) => sum + (n ?? 0), 0);

  /** Whether the table took it. Callers stage on `true` and keep on `false`. */
  const send = useCallback(
    async (
      emit: (
        cb: (
          res:
            | { ok: true; data: unknown }
            | { ok: false; error: { code: string; message: string } },
        ) => void,
      ) => void,
    ): Promise<boolean> => {
      const socket = getSocket();
      // Emitting on a closed socket queues the packet and flushes it after the
      // next handshake — on a new socket id, likely before the seat has been
      // reclaimed. Say so now instead of burning the ack timeout on it.
      if (!socket.connected) {
        socket.connect();
        setError('Reconnecting to the table. Try that again in a moment.');
        return false;
      }
      setBusy(true);
      try {
        await request<unknown>(emit);
        setError(null);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'The table said no.');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  /**
   * Staged stakes clear on the ack, never on the tap. A bet the table refuses
   * — a woken phone, a closed window — used to take the side bets down with
   * it, leaving the seat to rebuild the whole spread from memory.
   */
  const placeBet = async (amount: number): Promise<boolean> => {
    if (vm.isBaccarat) {
      if (spot === null) {
        setError('Pick Player, Banker or Tie first.');
        return false;
      }
      const staked = { ...pairBets };
      const ok = await send((cb) =>
        getSocket().emit('player_bet', { seatIndex: vm.mySeatIndex, amount, spot, sideBets: staked }, cb),
      );
      if (ok) setPairBets({});
      return ok;
    }
    if (vm.isThreecard) {
      // Pair Plus rides the same wager as the ante, baccarat-style.
      const staked = { ...pairBets };
      const ok = await send((cb) =>
        getSocket().emit('player_bet', { seatIndex: vm.mySeatIndex, amount, sideBets: staked }, cb),
      );
      if (ok) setPairBets({});
      return ok;
    }
    const staked = { ...sideBets };
    const ok = await send((cb) =>
      getSocket().emit('player_bet', { seatIndex: vm.mySeatIndex, amount, sideBets: staked }, cb),
    );
    if (ok) setSideBets({});
    return ok;
  };

  const act = (event: PlayAction) =>
    void send((cb) => getSocket().emit(event, { seatIndex: vm.mySeatIndex }, cb));

  const holdemAct = (action: HoldemActionType, amount?: number): Promise<boolean> =>
    send((cb) => getSocket().emit('player_holdem_action', { seatIndex: vm.mySeatIndex, action, amount }, cb));

  const rouletteAction = (
    action: 'bet' | 'clear_bet' | 'done' | 'rebet',
    extra: { betId?: RouletteBetId; num?: RouletteBetNum; amount?: number } = {},
  ) =>
    void send((cb) =>
      getSocket().emit('player_roulette_action', { seatIndex: vm.mySeatIndex, action, ...extra }, cb),
    );

  const kenoAction = (
    action: 'bet' | 'edit_bet' | 'clear_bet' | 'done' | 'rebet',
    extra: { spots?: number[]; amount?: number; ticketId?: number } = {},
  ) =>
    void send((cb) =>
      getSocket().emit('player_keno_action', { seatIndex: vm.mySeatIndex, action, ...extra }, cb),
    );

  const crapsAction = (
    action: 'bet' | 'clear_bet' | 'working' | 'press' | 'press_now' | 'ready' | 'roll',
    extra: {
      betId?: CrapsBetId;
      amount?: number;
      working?: boolean;
      press?: CrapsPressMode;
    } = {},
  ) =>
    void send((cb) =>
      getSocket().emit('player_craps_action', { seatIndex: vm.mySeatIndex, action, ...extra }, cb),
    );

  const threecardAction = (action: 'play' | 'fold') =>
    void send((cb) =>
      getSocket().emit('player_threecard_action', { seatIndex: vm.mySeatIndex, action }, cb),
    );

  const takeInsurance = (amount: number) => {
    setAnsweredInsurance((seats) => [...seats, vm.mySeatIndex]);
    void send((cb) => getSocket().emit('player_insurance', { seatIndex: vm.mySeatIndex, amount }, cb));
  };

  const clearBet = () =>
    void send((cb) => getSocket().emit('player_clear_bet', { seatIndex: vm.mySeatIndex }, cb));
  const sitOut = () =>
    void send((cb) => getSocket().emit('player_sit_out', { seatIndex: vm.mySeatIndex }, cb));
  const rebuy = () => void send((cb) => getSocket().emit('player_rebuy', cb));
  const takeSeat = (index: number) =>
    void send((cb) => getSocket().emit('player_take_seat', { seatIndex: index }, cb));
  const releaseSeat = (index: number) =>
    void send((cb) => getSocket().emit('player_release_seat', { seatIndex: index }, cb));

  const deal = () => void send((cb) => getSocket().emit('host_start_round', cb));
  const forceDeal = () => void send((cb) => getSocket().emit('host_force_deal', cb));
  const skipTurn = () => void send((cb) => getSocket().emit('host_skip_turn', cb));
  const setCardsHidden = (hidden: boolean) =>
    void send((cb) => getSocket().emit('host_set_cards_hidden', { hidden }, cb));
  const setLeaderboardShown = (shown: boolean) =>
    void send((cb) => getSocket().emit('host_set_leaderboard_shown', { shown }, cb));
  const setQrShown = (shown: boolean) =>
    void send((cb) => getSocket().emit('host_set_qr_shown', { shown }, cb));
  const setSoundOn = (on: boolean) =>
    void send((cb) => getSocket().emit('host_set_sound_on', { on }, cb));
  const setVolume = (patch: { sound?: number; ambience?: number }) =>
    void send((cb) => getSocket().emit('host_set_volume', patch, cb));
  const setCallouts = (patch: { mode?: CalloutMode; volume?: number }) =>
    void send((cb) => getSocket().emit('host_set_callouts', patch, cb));
  const updateRules = (patch: RulesPatch) =>
    void send((cb) => getSocket().emit('host_update_rules', patch, cb));
  const setGame = (game: GameType) =>
    void send((cb) => getSocket().emit('host_set_game', { game }, cb));
  const voteGame = (game: GameType) =>
    void send((cb) => getSocket().emit('player_game_vote', { game }, cb));
  const startPoll = () =>
    void send((cb) => getSocket().emit('host_start_game_poll', {}, cb));
  const endPoll = (apply: boolean) =>
    void send((cb) => getSocket().emit('host_end_game_poll', { apply }, cb));
  const startParty = (plan: PartyPlan) =>
    void send((cb) => getSocket().emit('host_start_party', { plan }, cb));
  const stopParty = () => void send((cb) => getSocket().emit('host_stop_party', cb));
  const skipPartyGame = () => void send((cb) => getSocket().emit('host_skip_party_game', cb));
  const updateHoldemRules = (patch: Record<string, unknown>) =>
    void send((cb) => getSocket().emit('host_update_holdem_rules', patch, cb));
  const updateBaccaratRules = (patch: BaccaratRulesPatch) =>
    void send((cb) => getSocket().emit('host_update_baccarat_rules', patch, cb));
  const updateRouletteRules = (patch: RouletteRulesPatch) =>
    void send((cb) => getSocket().emit('host_update_roulette_rules', patch, cb));

  const updateKenoRules = (patch: KenoRulesPatch) =>
    void send((cb) => getSocket().emit('host_update_keno_rules', patch, cb));
  const updateCrapsRules = (patch: CrapsRulesPatch) =>
    void send((cb) => getSocket().emit('host_update_craps_rules', patch, cb));
  const updateThreecardRules = (patch: ThreecardRulesPatch) =>
    void send((cb) => getSocket().emit('host_update_threecard_rules', patch, cb));
  const claimHost = () => void send((cb) => getSocket().emit('host_claim', cb));
  const transferHost = (target: string) =>
    void send((cb) => getSocket().emit('host_transfer', { playerId: target }, cb));
  const kick = (target: string) =>
    void send((cb) => getSocket().emit('host_kick', { playerId: target }, cb));
  const grant = (target: string, amount: number) =>
    void send((cb) => getSocket().emit('host_grant_chips', { playerId: target, amount }, cb));

  const closeTable = async () => {
    setBusy(true);
    try {
      await request<null>((cb) => getSocket().emit('host_close_room', cb));
    } catch {
      /* the room is closing either way */
    } finally {
      playerIdentity.clearSeat();
      wantsSeat.current = false;
      setSeat(null);
      setState(null);
      setBusy(false);
    }
  };

  const toggleReady = async () => {
    setBusy(true);
    try {
      await request<null>((cb) =>
        getSocket().emit('player_set_ready', { ready: !vm.ready }, cb),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update.');
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    setBusy(true);
    try {
      await request<null>((cb) => getSocket().emit('player_leave', cb));
    } catch {
      /* leaving anyway */
    } finally {
      playerIdentity.clearSeat();
      wantsSeat.current = false;
      setSeat(null);
      setState(null);
      setBusy(false);
    }
  };

  /**
   * Move between the phone's screens. Stepping into the lobby re-reads the
   * member card and the lineup, so chips and the table list are never a round
   * stale by the time they are looked at.
   */
  const showView = (next: PlayView) => {
    setView(next);
    if (next === 'casino') {
      void refreshMe();
      void refreshMarket();
      void refreshFloor();
      void refreshCasino();
    }
  };

  /** Choose a table before sitting, or try another one from the casino screen. */
  const sitHere = (tableCode: string) => {
    setCode(tableCode);
    setAlreadySeated(false);
    if (seat) void join(tableCode, seat.name);
  };

  if (!seat) {
    return (
      <main className={styles.wrap} style={themeStyle(casino?.theme)}>
        <h1 className={styles.title}>
          {casinoCode ? 'Welcome in' : 'Take a seat'}
          <AlphaBadge />
        </h1>
        {vm.notice && <p className={styles.notice}>{vm.notice}</p>}
        {casinoCode && (
          <>
            {dressesVenue && ownerPanelVm && (
              <CasinoOwnerPanel
                vm={ownerPanelVm}
                busy={busy}
                onFelt={changeFelt}
                onChips={changeChips}
                onDeck={changeDeck}
              />
            )}
            <CasinoLobby
              vm={lobbyVm}
              market={marketVm}
              floor={floorVm}
              onTab={setLobbyTab}
              onSit={sitHere}
              onLoadMoreTrades={loadMoreTrades}
              onLoadMoreLedger={loadMoreLedger}
              tradesBusy={tradesBusy}
              ledgerBusy={ledgerBusy}
              floorWindow={floorWindow}
              onFloorWindow={changeFloorWindow}
            />
          </>
        )}
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            void join(code, name);
          }}
        >
          {/* Under a casino the code came from the QR — only a name is needed. */}
          {!casinoCode && (
            <>
              <label className={styles.label} htmlFor="room">
                Room code
              </label>
              <input
                id="room"
                className={styles.codeInput}
                value={code}
                onChange={(e) => setCode(normalizeRoomCode(e.target.value))}
                placeholder="ABCD"
                autoComplete="off"
                autoCapitalize="characters"
                autoCorrect="off"
                inputMode="text"
                maxLength={4}
              />
            </>
          )}

          {joinProfile.signedIn ? (
            <div className={styles.joinWho} aria-label="Signed in">
              <span
                className={styles.joinWhoDisc}
                style={{ '--accent': joinProfile.accentHex } as CSSProperties}
                aria-hidden="true"
              >
                {joinProfile.initials}
              </span>
              <div className={styles.joinWhoText}>
                <p className={styles.joinWhoLabel}>Signed in as</p>
                <p className={styles.joinWhoName}>
                  <span className={styles.joinWhoNameText}>{joinProfile.name}</span>
                  <ProfileBadge />
                </p>
                <p className={styles.joinWhoHint}>{joinProfile.hint}</p>
              </div>
              <Link
                className={styles.joinWhoLink}
                to={casinoCode ? `/profile?casino=${casinoCode}` : '/profile'}
              >
                Profile
              </Link>
            </div>
          ) : (
            <p className={styles.joinGuest}>
              {joinProfile.hint}{' '}
              <Link className={styles.joinWhoLink} to="/casino">
                Sign in
              </Link>
            </p>
          )}

          <label className={styles.label} htmlFor="name">
            {hostName ? 'Host' : 'Nickname'}
          </label>
          <input
            id="name"
            className={styles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Kris"
            maxLength={MAX_NAME_LENGTH}
            autoComplete="nickname"
            // The host's seat name comes from their account; it is reserved
            // here, so there is nothing to choose.
            readOnly={hostName !== null}
          />

          {error && <p className={styles.error}>{error}</p>}

          <button
            className={styles.primary}
            type="submit"
            disabled={busy || code.length !== 4 || !name.trim() || (casinoCode !== null && !lobbyVm.canSit)}
          >
            {busy ? 'Joining…' : casinoCode ? lobbyVm.sitLabel : 'Join the table'}
          </button>
        </form>

        {/* A signed-in account at the bare join page opens or runs its own
            venues from here, without walking through somebody's casino. */}
        {!casinoCode && session && <YourCasinos />}

        {/* Deal and host settings live on My controls after sitting — not on
            the table lineup where they would fire a round before anyone joined. */}
        <PlayForFun />
        <div className={styles.feedbackRow}>
          <FeedbackButton casinoCode={casinoCode} tableCode={code || null} path="/play" />
        </div>
      </main>
    );
  }

  return (
    <main className={styles.wrap} style={themeStyle(casino?.theme)}>
      {splash && (
        <GameSplash
          key={splash.key}
          vm={gameSplashVM(splash.game, { from: splash.from })}
          size="phone"
          holdMs={PHONE_SPLASH_MS}
          onDismiss={dismissSplash}
        />
      )}
      <header className={styles.seatHeader}>
        <div>
          <p className={styles.label}>
            Room {seat.code}
            <AlphaBadge />
          </p>
          <h1 className={styles.name}>
            {seat.name}
            {seatHasProfile && <ProfileBadge />}
          </h1>
          {vm.gameBadge && <p className={styles.gameBadge}>{vm.gameBadge}</p>}
          {vm.amHost && <p className={styles.runnerBadge}>You run the table</p>}
        </div>
        <div className={styles.headerRight}>
          <span className={vm.online ? styles.pillOk : styles.pillWarn}>{vm.seatPill}</span>
          {/* This phone's own sound, next to the profile door: the TV plays
              for the room, this plays for whatever is in this player's ears. */}
          <DeviceAudio mode={calloutMode} onModeChange={changeCalloutMode} />
          {/* The one standing way off this page and into the profile. It wears
              the hue picked there, so a pick is visible from the felt itself
              rather than only on the page that set it. */}
          <Link
            className={styles.profileDisc}
            style={{ '--accent': profileHex(me?.color ?? null) } as CSSProperties}
            to={casinoCode ? `/profile?casino=${casinoCode}` : '/profile'}
            aria-label="Your profile"
            title="Your profile"
          >
            {profileInitials(me?.name ?? seat.name)}
          </Link>
        </div>
      </header>

      {vm.banner && (
        <div className={styles.notice}>
          <p className={styles.dim}>{vm.banner}</p>
          {/* The one way out of a table frozen behind a dead phone. */}
          {vm.canClaimHost && (
            <button type="button" className={styles.secondary} onClick={claimHost} disabled={busy}>
              Take over the table
            </button>
          )}
        </div>
      )}

      <nav className={styles.viewNav} aria-label="Screens">
        {vm.navTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={tab.active ? styles.viewNavTabOn : styles.viewNavTab}
            aria-current={tab.active ? 'page' : undefined}
            onClick={() => showView(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {view === 'casino' && (
        <>
          {dressesVenue && ownerPanelVm && (
            <CasinoOwnerPanel
              vm={ownerPanelVm}
              busy={busy}
              onFelt={changeFelt}
              onChips={changeChips}
              onDeck={changeDeck}
            />
          )}
          <CasinoLobby
            vm={lobbyVm}
            market={marketVm}
            floor={floorVm}
            onTab={setLobbyTab}
            onSit={sitHere}
            onLoadMoreTrades={loadMoreTrades}
            onLoadMoreLedger={loadMoreLedger}
            tradesBusy={tradesBusy}
            ledgerBusy={ledgerBusy}
            floorWindow={floorWindow}
            onFloorWindow={changeFloorWindow}
          />
        </>
      )}

      {vm.seatTabs.length > 1 && (
        <nav className={styles.seatTabs} aria-label="Your seats">
          {vm.seatTabs.map((tab) => (
            <button
              key={tab.index}
              type="button"
              className={tab.active ? styles.seatTabOn : styles.seatTab}
              onClick={() => setFocusSeat(tab.index)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      )}

      {view === 'table' && state && vm.tableView ? (
        <section className={styles.tableView} aria-label="Whole table, large text">
          {vm.tableView.craps ? (
            <SeatHuesContext.Provider value={hues}>
              <CrapsTable
                rules={vm.tableView.craps.rules}
                point={vm.tableView.craps.point}
                stacks={vm.tableView.craps.stacks}
                marks={vm.tableView.craps.marks}
                head={
                  <div className={styles.tableDealer}>
                    <p className={styles.tableName}>{vm.tableView.dealerLabel}</p>
                    <p className={styles.tableValue}>{vm.tableView.dealerValueLine}</p>
                    {vm.tableView.craps.callLine && (
                      <p className={styles.tableResult}>{vm.tableView.craps.callLine}</p>
                    )}
                    {vm.tableView.craps.shooterLine && (
                      <p className={styles.tableChips}>{vm.tableView.craps.shooterLine}</p>
                    )}
                  </div>
                }
              />

              {vm.tableView.rows.map((row) => (
                <div
                  key={row.index}
                  className={[
                    styles.tableSeat,
                    row.mine ? styles.tableSeatMine : '',
                    row.acting ? styles.tableSeatActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <p className={styles.tableName}>{row.nameLine}</p>
                  <p className={styles.tableValue}>{row.fallback}</p>
                  {row.handLabel && <p className={styles.tableResult}>{row.handLabel}</p>}
                  <p className={styles.tableChips}>{row.chipsLine}</p>
                </div>
              ))}
            </SeatHuesContext.Provider>
          ) : vm.tableView.baccarat ? (
            <>
              {(
                [
                  [
                    vm.tableView.baccarat.playerLabel,
                    vm.tableView.baccarat.playerCards,
                    vm.tableView.baccarat.playerTotal,
                  ],
                  [
                    vm.tableView.baccarat.bankerLabel,
                    vm.tableView.baccarat.bankerCards,
                    vm.tableView.baccarat.bankerTotal,
                  ],
                ] as const
              ).map(([label, cards, total]) => (
                <div key={label} className={styles.tableDealer}>
                  <p className={styles.tableName}>{label}</p>
                  {cards.length > 0 ? (
                    <>
                      <CardRow cards={cards} large />
                      <p className={styles.tableValue}>{total ?? '…'}</p>
                    </>
                  ) : (
                    <p className={styles.tableValue}>No cards yet</p>
                  )}
                </div>
              ))}

              {vm.tableView.rows.map((row) => (
                <div
                  key={row.index}
                  className={[styles.tableSeat, row.mine ? styles.tableSeatMine : '']
                    .filter(Boolean)
                    .join(' ')}
                >
                  <p className={styles.tableName}>{row.nameLine}</p>
                  {row.cards ? (
                    <div className={styles.tableHand}>
                      <CardRow cards={row.cards} large />
                    </div>
                  ) : (
                    <p className={styles.tableValue}>{row.fallback}</p>
                  )}
                  {row.handLabel && <p className={styles.tableResult}>{row.handLabel}</p>}
                  {row.committed > 0 && (
                    <p className={styles.tableValue}>In for {row.committed.toLocaleString()}</p>
                  )}
                  <p className={styles.tableChips}>{row.chipsLine}</p>
                </div>
              ))}
            </>
          ) : vm.tableView.holdem ? (
            <>
              <div className={styles.tableDealer}>
                <p className={styles.tableName}>{vm.tableView.streetLabel}</p>
                {vm.tableView.boardCards.length > 0 ? (
                  <CardRow cards={vm.tableView.boardCards} large />
                ) : (
                  <p className={styles.tableValue}>No cards yet</p>
                )}
                <p className={styles.tableValue}>{vm.tableView.potLine}</p>
              </div>

              {vm.tableView.rows.map((row) => (
                <div
                  key={row.index}
                  className={[
                    styles.tableSeat,
                    row.mine ? styles.tableSeatMine : '',
                    row.acting ? styles.tableSeatActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <p className={styles.tableName}>{row.nameLine}</p>
                  {row.cards ? (
                    <div className={styles.tableHand}>
                      <CardRow cards={row.cards} large />
                      {row.handLabel && <p className={styles.tableValue}>{row.handLabel}</p>}
                    </div>
                  ) : (
                    <p className={styles.tableValue}>{row.fallback}</p>
                  )}
                  {row.committed > 0 && (
                    <p className={styles.tableValue}>In for {row.committed.toLocaleString()}</p>
                  )}
                  <p className={styles.tableChips}>{row.chipsLine}</p>
                </div>
              ))}
            </>
          ) : (
            <>
              <div className={styles.tableDealer}>
                <p className={styles.tableName}>{vm.tableView.dealerLabel}</p>
                {vm.tableView.dealerCards.length > 0 ? (
                  <>
                    <CardRow cards={vm.tableView.dealerCards} large />
                    <p className={styles.tableValue}>{vm.tableView.dealerValueLine}</p>
                  </>
                ) : (
                  <p className={styles.tableValue}>No cards yet</p>
                )}
              </div>

              {vm.tableView.rows.map((row) => (
                <div
                  key={row.index}
                  className={[
                    styles.tableSeat,
                    row.mine ? styles.tableSeatMine : '',
                    row.acting ? styles.tableSeatActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <p className={styles.tableName}>{row.nameLine}</p>
                  {row.hands.length > 0 ? (
                    row.hands.map((h, i) => (
                      <div key={i} className={styles.tableHand}>
                        <CardRow cards={h.cards} large />
                        <p className={styles.tableValue}>
                          {h.valueLabel}
                          {h.bet > 0 ? ` · bet ${h.bet}` : ''}
                          {h.handTag ? ` · ${h.handTag}` : ''}
                        </p>
                        {vm.phase === 'payout' && h.result && (
                          <p className={styles.tableResult}>{h.result}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className={styles.tableValue}>{row.fallback}</p>
                  )}
                  <p className={styles.tableChips}>{row.chipsLine}</p>
                </div>
              ))}
            </>
          )}

          {vm.tableView.nobodySeated && (
            <p className={styles.tableValue}>Nobody has sat down yet.</p>
          )}

          {vm.deadline && (
            <Countdown endsAt={vm.deadline.endsAt} label={vm.deadline.label} />
          )}
        </section>
      ) : view === 'casino' ? null : (
        <>
          <section className={styles.chipCard}>
            <p className={styles.label}>Chips</p>
            <p className={styles.chips}>{vm.chipsAvailable.toLocaleString()}</p>
            {vm.deadline && (
              <Countdown endsAt={vm.deadline.endsAt} label={vm.deadline.label} />
            )}
          </section>

          <section
            ref={stageRef}
            className={[
              styles.stage,
              vm.isKeno && styles.kenoStage,
              vm.kenoView?.classicColors && styles.kenoStageClassic,
            ]
              .filter(Boolean)
              .join(' ')}
            tabIndex={-1}
            aria-label="The roller"
          >
            {vm.isCraps && vm.crapsView ? (
              <>
                <div className={styles.handItem}>
                  <p className={styles.label}>{vm.crapsView.puckLabel}</p>
                  <CrapsDice
                    size="phone"
                    rollNumber={vm.crapsView.rollNumber}
                    dice={vm.crapsView.lastRoll}
                    placeholder={vm.crapsView.placeholder}
                    caption={vm.crapsView.rollLine}
                  />
                </div>
                {vm.crapsView.shooterName && (
                  <p className={styles.dim}>
                    {vm.crapsView.amShooter
                      ? 'You have the dice.'
                      : `${vm.crapsView.shooterName} is shooting.`}
                  </p>
                )}
                {vm.crapsView.notices.length > 0 && (
                  <ul className={styles.sideList}>
                    {vm.crapsView.notices.map((n) => (
                      <li
                        key={n.key}
                        className={
                          n.tone === 'win'
                            ? styles.sideWin
                            : n.tone === 'loss'
                              ? styles.sideLoss
                              : styles.dim
                        }
                      >
                        {n.text}
                      </li>
                    ))}
                  </ul>
                )}
                {vm.crapsView.result && (
                  <p
                    className={
                      vm.crapsView.result.win
                        ? styles.sideWin
                        : vm.crapsView.result.loss
                          ? styles.sideLoss
                          : styles.dim
                    }
                  >
                    {vm.crapsView.result.label}
                  </p>
                )}
                {vm.crapsView.readyLabel && <p className={styles.dim}>{vm.crapsView.readyLabel}</p>}
                {vm.crapsView.history.length > 0 && (
                  <div>
                    <p className={styles.label}>Recent rolls</p>
                    <ul className={styles.pocketRoad} aria-label="Recent rolls, oldest first">
                      {vm.crapsView.history.map((r) => (
                        <li key={r.key} className={styles.pocketBead} title={`${r.dice[0]} and ${r.dice[1]}`}>
                          {r.total}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {vm.sittingOut && (
                  <p className={styles.dim}>
                    {vm.phase === 'rolling'
                      ? 'Sat out the come-out — drop a chip to join the hand.'
                      : 'Sitting this hand out.'}
                  </p>
                )}
              </>
            ) : vm.isRoulette && vm.rouletteView ? (
              <>
                <div className={styles.handItem}>
                  <p className={styles.label}>
                    {vm.rouletteView.pocket !== null ? 'Pocket' : 'The wheel'}
                  </p>
                  <RouletteWheel
                    size="phone"
                    wheelOrder={vm.rouletteView.wheel.wheelOrder}
                    landedIndex={vm.rouletteView.wheel.landedIndex}
                    pocket={vm.rouletteView.wheel.pocket}
                    pocketColor={vm.rouletteView.wheel.pocketColor}
                    resultLine={vm.rouletteView.result?.label ?? null}
                    endsAt={vm.deadline?.endsAt ?? null}
                    placeholder={vm.rouletteView.placeholder}
                  />
                </div>
                {vm.rouletteView.result && (
                  <p
                    className={
                      vm.rouletteView.result.win
                        ? styles.sideWin
                        : vm.rouletteView.result.loss
                          ? styles.sideLoss
                          : styles.dim
                    }
                  >
                    {vm.rouletteView.result.label}
                  </p>
                )}
                {vm.rouletteView.doneLabel && (
                  <p className={styles.dim}>{vm.rouletteView.doneLabel}</p>
                )}
                {vm.rouletteView.history.length > 0 && (
                  <div>
                    <p className={styles.label}>Past pockets</p>
                    <ul className={styles.pocketRoad} aria-label="Past pockets, oldest first">
                      {vm.rouletteView.history.map((h, i) => (
                        <li
                          key={`${i}-${h.pocket}`}
                          className={[
                            styles.pocketBead,
                            h.color === 'red'
                              ? styles.pocketRed
                              : h.color === 'black'
                                ? styles.pocketBlack
                                : styles.pocketGreen,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          {h.pocket}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {vm.sittingOut && <p className={styles.dim}>Sitting this spin out.</p>}
              </>
            ) : vm.isKeno && vm.kenoView ? (
              vm.kenoBet?.canDrop && !vm.kenoView.done ? (
                /* The picker below is the board while tickets can be written;
                   the stage only carries the word, never an empty box. */
                <p className={styles.dim}>{vm.kenoView.placeholder}</p>
              ) : (
                <>
                  <div className={styles.kenoBoardWrap}>
                    <p className={styles.label}>
                      {vm.kenoView.boardDraw ? 'The draw' : 'Keno board'}
                    </p>
                    <KenoBoard
                      size="phone"
                      draw={vm.kenoView.boardDraw}
                      tickets={vm.kenoView.myTickets}
                      reveal={kenoReveal}
                      classicColors={vm.kenoView.classicColors}
                    />
                  </div>
                  {vm.kenoView.result && (
                    <p
                      className={
                        vm.kenoView.result.win
                          ? styles.sideWin
                          : vm.kenoView.result.loss
                            ? styles.sideLoss
                            : styles.dim
                      }
                    >
                      {vm.kenoView.result.label}
                    </p>
                  )}
                  {vm.kenoView.doneLabel && (
                    <p className={styles.dim}>{vm.kenoView.doneLabel}</p>
                  )}
                  {vm.sittingOut && <p className={styles.dim}>Sitting this draw out.</p>}
                </>
              )
            ) : vm.isBaccarat && vm.baccaratView ? (
              <>
                {vm.baccaratView.hasCards ? (
                  <div className={styles.coup}>
                    <div className={styles.handItem}>
                      <p className={styles.label}>Player</p>
                      <CardRow cards={vm.baccaratView.playerCards} />
                      <p className={styles.handValue}>{vm.baccaratView.playerTotal ?? '…'}</p>
                    </div>
                    <div className={styles.handItem}>
                      <p className={styles.label}>Banker</p>
                      <CardRow cards={vm.baccaratView.bankerCards} />
                      <p className={styles.handValue}>{vm.baccaratView.bankerTotal ?? '…'}</p>
                    </div>
                  </div>
                ) : (
                  <p className={styles.dim}>{vm.baccaratView.placeholder}</p>
                )}
                {vm.baccaratView.outcomeLabel && (
                  <p className={styles.result}>{vm.baccaratView.outcomeLabel}</p>
                )}
                {vm.baccaratView.backedLabel && (
                  <p className={styles.dim}>{vm.baccaratView.backedLabel}</p>
                )}
                {vm.baccaratView.result && (
                  <p
                    className={
                      vm.baccaratView.result.win
                        ? styles.sideWin
                        : vm.baccaratView.result.loss
                          ? styles.sideLoss
                          : styles.dim
                    }
                  >
                    {vm.baccaratView.result.label}
                  </p>
                )}
                {vm.baccaratView.sideNotices.length > 0 && (
                  <ul className={styles.sideList}>
                    {vm.baccaratView.sideNotices.map((n) => (
                      <li
                        key={n.key}
                        className={
                          n.tone === 'win'
                            ? styles.sideWin
                            : n.tone === 'loss'
                              ? styles.sideLoss
                              : styles.dim
                        }
                      >
                        {n.text}
                      </li>
                    ))}
                  </ul>
                )}
                {vm.sittingOut && <p className={styles.dim}>Sitting this one out.</p>}
              </>
            ) : vm.isThreecard && vm.threecardView ? (
              <>
                {vm.threecardView.hasCards || vm.threecardView.dealerCards.length > 0 ? (
                  <div className={styles.cardLayout}>
                    {vm.threecardView.dealerCards.length > 0 && (
                      <div className={styles.handItem}>
                        <p className={styles.label}>Dealer</p>
                        <CardRow cards={vm.threecardView.dealerCards} />
                        {vm.threecardView.dealerLabel && (
                          <p className={styles.handValue}>
                            {vm.threecardView.dealerLabel}
                            {vm.threecardView.dealerQualifies === false
                              ? ' — doesn’t qualify'
                              : ''}
                          </p>
                        )}
                      </div>
                    )}
                    {vm.threecardView.hasCards && (
                      <div className={styles.handItem}>
                        <p className={styles.label}>Player</p>
                        <CardRow cards={vm.threecardView.myCards} />
                        <p className={styles.handValue}>
                          {vm.threecardView.myHandLabel ??
                            `${vm.threecardView.myCards.length} cards`}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className={styles.dim}>{vm.threecardView.placeholder}</p>
                )}
                {vm.threecardView.decisionHint && (
                  <p className={styles.dim}>{vm.threecardView.decisionHint}</p>
                )}
                {vm.threecardView.folded && <p className={styles.dim}>You mucked this hand.</p>}
                {vm.threecardView.notices.length > 0 && (
                  <ul className={styles.sideList}>
                    {vm.threecardView.notices.map((n) => (
                      <li
                        key={n.key}
                        className={
                          n.tone === 'win'
                            ? styles.sideWin
                            : n.tone === 'loss'
                              ? styles.sideLoss
                              : styles.dim
                        }
                      >
                        {n.text}
                      </li>
                    ))}
                  </ul>
                )}
                {vm.threecardView.result && (
                  <p
                    className={
                      vm.threecardView.result.win
                        ? styles.sideWin
                        : vm.threecardView.result.loss
                          ? styles.sideLoss
                          : styles.dim
                    }
                  >
                    {vm.threecardView.result.label}
                  </p>
                )}
                {vm.threecardView.history.length > 0 && (
                  <div>
                    <p className={styles.label}>Past hands</p>
                    <ul className={styles.pocketRoad} aria-label="Past hands, oldest first">
                      {vm.threecardView.history.map((h) => (
                        <li key={h.key} className={styles.pocketBead} title={h.label}>
                          {h.label.slice(0, 1)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {vm.sittingOut && <p className={styles.dim}>Sitting this hand out.</p>}
              </>
            ) : vm.isHoldem ? (
              <>
                {vm.holeCards.length > 0 || vm.boardCards.length > 0 ? (
                  <div className={styles.cardLayout}>
                    {/* Board (flop / turn / river) sits above the hole cards. */}
                    {vm.boardCards.length > 0 && (
                      <div className={styles.handItem}>
                        <p className={styles.label}>Dealer</p>
                        <CardRow cards={vm.boardCards} />
                        {vm.potLine && <p className={styles.handValue}>{vm.potLine}</p>}
                      </div>
                    )}
                    {vm.holeCards.length > 0 && (
                      <div className={styles.handItem}>
                        <p className={styles.label}>Player</p>
                        <CardRow cards={vm.holeCards} />
                        {vm.holeLabel && <p className={styles.handValue}>{vm.holeLabel}</p>}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className={styles.dim}>{vm.holdemPlaceholder}</p>
                )}
                {vm.sittingOut && <p className={styles.dim}>Sitting this one out.</p>}
                {vm.boardCards.length === 0 && vm.potLine && (
                  <p className={styles.dim}>{vm.potLine}</p>
                )}
                {vm.holdemWaitingForOther && (
                  <p className={styles.dim}>Waiting for another player to act…</p>
                )}
                {vm.holdemPayoutLine && (
                  <p
                    className={
                      vm.holdemPayoutLine.win
                        ? styles.sideWin
                        : vm.holdemPayoutLine.loss
                          ? styles.sideLoss
                          : styles.dim
                    }
                  >
                    {vm.holdemPayoutLine.label}
                  </p>
                )}
              </>
            ) : (
              <>
                {vm.dealerCards.length > 0 || vm.myHands.length > 0 ? (
                  <div className={styles.cardLayout}>
                    {vm.dealerCards.length > 0 && (
                      <div className={styles.handItem}>
                        <p className={styles.label}>Dealer</p>
                        <CardRow cards={vm.dealerCards} />
                        {vm.dealerValueLine && (
                          <p className={styles.handValue}>{vm.dealerValueLine}</p>
                        )}
                      </div>
                    )}
                    {vm.myHands.length > 0 && (
                      <div className={styles.handItem}>
                        <p className={styles.label}>Player</p>
                        <div className={vm.myHands.length > 1 ? styles.handGroup : undefined}>
                          {vm.myHands.map((h, i) => (
                            <div
                              key={i}
                              className={[styles.handItem, h.active ? styles.activeHand : '']
                                .filter(Boolean)
                                .join(' ')}
                            >
                              <CardRow cards={h.cards} />
                              <p className={styles.handValue}>{h.label}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className={styles.dim}>{vm.handPlaceholder}</p>
                )}
                {vm.sittingOut && <p className={styles.dim}>Sitting this one out.</p>}
                {vm.waitingForOtherPlayer && (
                  <p className={styles.dim}>Waiting for another player to act…</p>
                )}
                {vm.sideBetRows.length > 0 && (
                  <ul className={styles.sideList}>
                    {vm.sideBetRows.map((b) => (
                      <li key={b.key} className={b.win ? styles.sideWin : styles.sideLoss}>
                        {b.label}
                      </li>
                    ))}
                  </ul>
                )}
                {vm.insuranceResult && (
                  <p className={vm.insuranceResult.win ? styles.sideWin : styles.sideLoss}>
                    {vm.insuranceResult.label}
                  </p>
                )}
                {vm.results.map((r) => (
                  <p
                    key={r.key}
                    className={`${styles.result} ${r.className ? (styles[r.className] ?? '') : ''}`}
                  >
                    {r.label}
                  </p>
                ))}
              </>
            )}
          </section>
        </>
      )}

      {error && <p className={styles.error}>{error}</p>}

      {/* Actions stay on My controls — Casino is the lineup, The table is the
          felt, and neither should carry Deal or the chip rail underneath. */}
      {view === 'controls' && (
        <footer className={styles.controls}>
          {/* Top of the controls: a poll is the one thing the table is being
              asked for right now, so it outranks the betting UI under it. */}
          {vm.gamePoll && (
            <GamePollCard vm={vm.gamePoll} busy={busy} onVote={voteGame} />
          )}
          <PartyStrip party={state?.party} />

          {vm.insuranceOffered && (
            <div className={styles.actionRow}>
              <button
                className={styles.primary}
                disabled={busy}
                onClick={() => takeInsurance(vm.insuranceAmount)}
              >
                Insurance ({vm.insuranceAmount})
              </button>
              <button className={styles.secondary} disabled={busy} onClick={() => takeInsurance(0)}>
                No thanks
              </button>
            </div>
          )}

        {vm.baccaratBet && (
          <>
            <BaccaratBet
              rules={vm.baccaratBet.rules}
              chips={vm.chipsAvailable}
              spot={spot}
              onSpot={setSpot}
              pairBets={pairBets}
              onPairBets={setPairBets}
              busy={busy}
            />
            <ChipBet
              chips={vm.chipsAvailable - stagedPairBets}
              busy={busy || spot === null}
              minBet={vm.baccaratBet.rules.minBet}
              extra={stagedPairBets}
              onPlace={placeBet}
            />
            <button className={styles.ghost} disabled={busy} onClick={sitOut}>
              Sit this one out
            </button>
          </>
        )}

        {vm.threecardBet && (
          <>
            <ThreeCardBet
              rules={vm.threecardBet.rules}
              chips={Math.max(0, vm.chipsAvailable - 2 * vm.threecardBet.rules.minBet)}
              pairPlus={pairBets}
              onPairPlus={setPairBets}
              busy={busy}
            />
            <ChipBet
              chips={Math.floor(Math.max(0, vm.chipsAvailable - stagedPairBets) / 2)}
              busy={busy}
              minBet={vm.threecardBet.rules.minBet}
              extra={stagedPairBets}
              onPlace={placeBet}
            />
            <button className={styles.ghost} disabled={busy} onClick={sitOut}>
              Sit this one out
            </button>
          </>
        )}

        {vm.isThreecard && vm.threecardView?.canDecide && (
          <div className={styles.actionRow}>
            <button
              className={styles.primary}
              disabled={busy || !vm.threecardView.canPlay}
              onClick={() => threecardAction('play')}
            >
              Play ({vm.threecardView.ante.toLocaleString()})
            </button>
            <button className={styles.secondary} disabled={busy} onClick={() => threecardAction('fold')}>
              Fold
            </button>
          </div>
        )}

        {vm.isRoulette && vm.rouletteBet && vm.rouletteView && (
          <SeatHuesContext.Provider value={hues}>
            <RouletteBet
              rules={vm.rouletteBet.rules}
              chips={vm.chipsAvailable}
              stacks={vm.rouletteBet.stacks}
              pocket={vm.rouletteBet.pocket}
              marks={vm.rouletteBet.marks}
              bets={vm.rouletteView.myBets}
              lastBets={vm.rouletteView.lastBets}
              staked={vm.rouletteView.myStaked}
              done={vm.rouletteView.done}
              canDrop={vm.rouletteBet.canDrop}
              busy={busy}
              onPlace={(betId, num, amount) => rouletteAction('bet', { betId, num, amount })}
              onClear={(stack) =>
                rouletteAction('clear_bet', stack ? { betId: stack.id, num: stack.num } : {})
              }
              onRebet={vm.rouletteBet.canDrop ? () => rouletteAction('rebet') : undefined}
              onDone={() => rouletteAction('done')}
            />
            {vm.canSitOut && (
              <button className={styles.ghost} disabled={busy} onClick={sitOut}>
                Sit this spin out
              </button>
            )}
          </SeatHuesContext.Provider>
        )}

        {vm.isKeno && vm.kenoBet && vm.kenoView && (
          <>
            <KenoBet
              rules={vm.kenoBet.rules}
              chips={vm.chipsAvailable}
              tickets={vm.kenoView.myTickets}
              lastTickets={vm.kenoView.lastTickets}
              staked={vm.kenoView.myStaked}
              done={vm.kenoView.done}
              canDrop={vm.kenoBet.canDrop}
              busy={busy}
              onAdd={(spots, amount) => kenoAction('bet', { spots, amount })}
              onEdit={(ticketId, spots, amount) => kenoAction('edit_bet', { ticketId, spots, amount })}
              onClear={(ticketId) => kenoAction('clear_bet', ticketId ? { ticketId } : {})}
              onRebet={vm.kenoBet.canDrop ? () => kenoAction('rebet') : undefined}
              onDone={() => kenoAction('done')}
            />
            {vm.canSitOut && (
              <button className={styles.ghost} disabled={busy} onClick={sitOut}>
                Sit this draw out
              </button>
            )}
          </>
        )}

        {vm.isCraps && vm.crapsBet && vm.crapsView && (
          <SeatHuesContext.Provider value={hues}>
            <CrapsBet
              rules={vm.crapsBet.rules}
              point={vm.crapsBet.point}
              chips={vm.chipsAvailable}
              stacks={vm.crapsBet.stacks}
              marks={vm.crapsBet.marks}
              bets={vm.crapsBet.myBets}
              hits={vm.crapsBet.hits}
              staked={vm.crapsView.myStaked}
              ready={vm.crapsView.ready}
              canDrop={vm.crapsBet.canDrop}
              showReady={vm.crapsBet.showReady}
              rolling={vm.crapsBet.rolling}
              canRoll={vm.crapsBet.canRoll}
              shooterName={vm.crapsBet.shooterName}
              busy={busy}
              onPlace={(betId, amount) => crapsAction('bet', { betId, amount })}
              onClear={(betId) => crapsAction('clear_bet', betId ? { betId } : {})}
              onWorking={(betId, working) => crapsAction('working', { betId, working })}
              onPress={(betId, press) => crapsAction('press', { betId, press })}
              onPressNow={(betId, press) => crapsAction('press_now', { betId, press })}
              onReady={() => crapsAction('ready')}
              onRoll={() => crapsAction('roll')}
            />
            {vm.canSitOut && (
              <button className={styles.ghost} disabled={busy} onClick={sitOut}>
                Sit this hand out
              </button>
            )}
          </SeatHuesContext.Provider>
        )}

        {vm.canBet && vm.betControls && !vm.isBaccarat && (
          <>
            <SideBetPicker
              enabled={vm.betControls.sideBets}
              max={vm.betControls.sideBetMax}
              budget={vm.chipsAvailable - stagedSideBets}
              value={sideBets}
              busy={busy}
              onChange={setSideBets}
            />
            <ChipBet
              chips={vm.chipsAvailable - stagedSideBets}
              busy={busy}
              minBet={vm.betControls.minBet}
              extra={stagedSideBets}
              onPlace={placeBet}
            />
            <button className={styles.ghost} disabled={busy} onClick={sitOut}>
              Sit this one out
            </button>
          </>
        )}

        {vm.canClearBet && (
          <button className={styles.secondary} disabled={busy} onClick={clearBet}>
            {vm.clearBetLabel}
          </button>
        )}

        {vm.canRebuy && (
          <button className={styles.primary} disabled={busy} onClick={rebuy}>
            {vm.rebuyLabel}
          </button>
        )}

        {vm.holdemActions && (
          <HoldemActions
            options={vm.holdemActions.options}
            chips={vm.holdemActions.chips}
            potTotal={vm.holdemActions.potTotal}
            currentBet={vm.holdemActions.currentBet}
            bigBlind={vm.holdemActions.bigBlind}
            busy={busy}
            onAct={holdemAct}
          />
        )}

        {vm.isMyTurn && (
          <div className={styles.actionRow}>
            {vm.canHit && (
              <button className={styles.primary} disabled={busy} onClick={() => act('player_hit')}>
                Hit
              </button>
            )}
            {vm.canStand && (
              <button
                ref={standWatch.ref}
                className={styles.primary}
                disabled={busy}
                onClick={() => act('player_stand')}
              >
                Stand
              </button>
            )}
            {vm.canDouble && (
              <button
                className={styles.secondary}
                disabled={busy}
                onClick={() => act('player_double')}
              >
                Double
              </button>
            )}
            {vm.canSplit && (
              <button
                className={styles.secondary}
                disabled={busy}
                onClick={() => act('player_split')}
              >
                Split
              </button>
            )}
            {vm.canSurrender && (
              <button
                className={styles.ghost}
                disabled={busy}
                onClick={() => act('player_surrender')}
              >
                Surrender
              </button>
            )}
          </div>
        )}

        <FloatAction
          show={vm.isMyTurn && vm.canStand && standWatch.offscreen}
          buttons={[
            ...(vm.canHit
              ? [{ label: 'Hit', disabled: busy, onClick: () => act('player_hit') }]
              : []),
            // Stand is the pill that gives ground once Hit is on the bar beside it.
            { label: 'Stand', quiet: vm.canHit, disabled: busy, onClick: () => act('player_stand') },
          ]}
        />

        {vm.showReadyToggle && (
          <button
            className={vm.ready ? styles.secondary : styles.primary}
            onClick={() => void toggleReady()}
            disabled={busy}
          >
            {vm.readyLabel}
          </button>
        )}

        {vm.canDeal && (
          <button className={styles.primary} disabled={busy} onClick={deal}>
            {vm.dealLabel}
          </button>
        )}

        {vm.canTakeAnother && (
          <div className={styles.seatPicker}>
            <p className={styles.label}>Play another seat</p>
            <div className={styles.actionRow}>
              {vm.openSeats.map((index) => (
                <button
                  key={index}
                  className={styles.secondary}
                  disabled={busy}
                  onClick={() => takeSeat(index)}
                >
                  Seat {index + 1}
                </button>
              ))}
            </div>
          </div>
        )}

        {vm.canReleaseSeat && (
          <button
            className={styles.ghost}
            disabled={busy}
            onClick={() => releaseSeat(vm.mySeatIndex)}
          >
            Give up seat {vm.mySeatIndex + 1}
          </button>
        )}

        {vm.showHostPanel && state && (
          <HostPanel
            state={state}
            playerId={seat.playerId}
            busy={busy}
            onRules={updateRules}
            onSetGame={setGame}
            onStartPoll={startPoll}
            onEndPoll={endPoll}
            onStartParty={startParty}
            onStopParty={stopParty}
            onSkipPartyGame={skipPartyGame}
            onHoldemRules={updateHoldemRules}
            onBaccaratRules={updateBaccaratRules}
            onRouletteRules={updateRouletteRules}
            onKenoRules={updateKenoRules}
            onCrapsRules={updateCrapsRules}
            onThreecardRules={updateThreecardRules}
            onForceDeal={forceDeal}
            onSkipTurn={skipTurn}
            onToggleCards={setCardsHidden}
            onToggleLeaderboard={casinoCode ? setLeaderboardShown : null}
            onToggleQr={casino?.isPrivate ? null : setQrShown}
            onToggleSound={setSoundOn}
            onVolume={setVolume}
            onCallouts={setCallouts}
            onTransfer={transferHost}
            onKick={kick}
            onGrant={grant}
          />
        )}

        <button className={styles.ghost} onClick={() => void leave()} disabled={busy}>
          Leave table
        </button>
        {vm.amHost && (!casinoCode || adhocVenue) && (
          <button className={styles.ghost} onClick={() => void closeTable()} disabled={busy}>
            Close table
          </button>
        )}
        <BuildStamp className={styles.buildStamp} />
        {/* Next to the build, because the build is the first thing a bug
            report needs and the two travel together. */}
        <div className={styles.feedbackRow}>
          <FeedbackButton
            casinoCode={casinoCode}
            tableCode={seat.code}
            gameType={state?.gameType ?? null}
            path="/play"
          />
        </div>
      </footer>
      )}
    </main>
  );
}
